import { AmountDueKind, BillingReason, SubscriptionStatus } from "./billingCodes";
import { formatBillingDate } from "./billingDates";

// What a payment owed comes to, as billing-status reports it before anybody
// confirms it: `amountDue` — { amount, currency, kind, invoiceCount } — set while
// the subscription is past_due, unpaid or incomplete, or has ended as an annual
// plan with its invoices still open (OUTSTANDING_DEBT), and null otherwise (and
// on the status a sign in or a refresh carries, which reads no invoices).

// The subscription statuses billing-status can report an amount for.
const OWING_STATUSES = new Set([
   SubscriptionStatus.PAST_DUE,
   SubscriptionStatus.UNPAID,
   SubscriptionStatus.INCOMPLETE
]);

/** Whether a status is one that owes money, whether or not it carries the amount yet. */
export const owesPayment = (status) =>
   OWING_STATUSES.has(status?.subscriptionStatus) || status?.reason === BillingReason.OUTSTANDING_DEBT;

/**
 * An amount in a currency's smallest unit (cents) as money: 9900 "usd" →
 * "$99.00". How many decimals the unit has is the currency's own (none for
 * yen). Printed the way the plan cards print prices, so "$" rather than "US$".
 * Null for anything that isn't an amount and a currency.
 */
export const formatMoney = (amount, currency, locale = "en-US") => {
   if (!Number.isFinite(amount) || typeof currency !== "string" || !currency.trim()) return null;

   try {
      const format = new Intl.NumberFormat(locale, {
         style: "currency",
         currency: currency.trim().toUpperCase()
      });
      const decimals = format.resolvedOptions().maximumFractionDigits;
      return format.format(amount / 10 ** decimals);
   } catch {
      return null;
   }
};

/** A status's amount due as money, or null when it reports none. */
export const formatAmountDue = (status) =>
   status?.amountDue ? formatMoney(status.amountDue.amount, status.amountDue.currency) : null;

/**
 * What confirming will charge, in a sentence, or null when the status reports
 * no amount. A monthly plan back after its access stopped starts a new month
 * today and is not charged the months it missed; anything else pays its unpaid
 * invoices and keeps its dates — on an annual plan, its commitment's end too.
 */
export const describeAmountDue = (status) => {
   const amount = formatAmountDue(status);
   if (!amount) return null;

   const { kind, invoiceCount } = status.amountDue;

   if (kind === AmountDueKind.RESTART_PERIOD) {
      return `We'll charge ${amount} for one month, starting today. The months you missed aren't charged.`;
   }

   const count = Number.isInteger(invoiceCount) && invoiceCount > 0 ? invoiceCount : 1;
   const payments = `${count} unpaid monthly payment${count === 1 ? "" : "s"}`;

   // An annual plan that ended unpaid: its balance, and nothing after it.
   if (kind === AmountDueKind.OUTSTANDING_DEBT) {
      const ended = formatBillingDate(status.commitmentEnd);
      return `We'll charge ${amount} for the ${payments} of your annual plan${
         ended ? `, which ended on ${ended}` : ""
      }. This settles your balance; it doesn't start a new subscription.`;
   }

   if (kind !== AmountDueKind.OVERDUE_INVOICES) return `We'll charge ${amount} now.`;

   // A subscription that never got its first payment through.
   if (status.subscriptionStatus === SubscriptionStatus.INCOMPLETE) {
      return `We'll charge ${amount} for your first monthly payment.`;
   }

   const charge = `We'll charge ${amount} for ${count} overdue monthly payment${count === 1 ? "" : "s"}.`;

   if (status.isMonthly !== false) return charge;

   const commitmentEnds = formatBillingDate(status.commitmentEnd);
   return `${charge} The remaining months are billed monthly as before${
      commitmentEnds ? `, and your commitment still ends on ${commitmentEnds}` : ""
   }.`;
};

/**
 * An unpaid annual plan is set to end at the end of its contract, with every
 * month up to then still owed, unless it is paid first: "Your annual plan ends on
 * Oct 1, 2027 unless the $213.00 owed is paid." Null for anything else, and for
 * a status that doesn't say when (`cancelAt`) or how much.
 */
export const describeUnpaidPlanEnd = (status) => {
   if (status?.subscriptionStatus !== SubscriptionStatus.UNPAID || status?.isMonthly !== false) return null;

   const endsOn = formatBillingDate(status.cancelAt ?? status.commitmentEnd);
   const amount = formatAmountDue(status);
   if (!endsOn || !amount) return null;

   return `Your annual plan ends on ${endsOn} unless the ${amount} owed is paid.`;
};
